import { useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  Bath,
  BedDouble,
  ChevronLeft,
  ChevronRight,
  Expand,
  ImageOff,
  Ruler,
  X,
} from "lucide-react";

import { Hint } from "@/design-system/Hint.tsx";
import { ageColor, ageInk } from "@/listings/listing.freshness.ts";
import {
  ageInDays,
  bathLabel,
  bedLabel,
  fullDate,
  money,
  isLayoutOnlyTitle,
  spelledAge,
  sourceName,
} from "@/listings/listing.format.ts";
import { usePinchZoom } from "@/platform/use-pinch-zoom.ts";
import { useSwipe } from "@/platform/use-swipe.ts";
import { HideButton } from "@/listings/HideButton.tsx";
import { ShareButton } from "@/share/ShareButton.tsx";
import { TrackButton } from "@/listings/TrackButton.tsx";
import { TravelPanel } from "@/travel/TravelPanel.tsx";
import type { Anchor, Listing } from "@housing/shared";
import { cn } from "@/design-system/cn.ts";

interface ListingCardProps {
  listing: Listing;
  /** Destination for the travel times; null when the server has no anchor set. */
  anchor: Anchor | null;
  onClose: () => void;
  /** Mobile renders this as a bottom sheet; desktop as a map-anchored popup. */
  variant: "sheet" | "popup";
}

type PointerHandlers = React.HTMLAttributes<HTMLElement>;

/**
 * Run two pointer recognisers on one surface.
 *
 * Spreading both onto the same element does not do this — they share key names,
 * so the second silently replaces the first. That is how pinch-to-zoom took
 * swipe-to-page's handlers with it and paging stopped working in fullscreen.
 */
const both =
  (x?: React.PointerEventHandler<HTMLElement>, y?: React.PointerEventHandler<HTMLElement>) =>
  (e: React.PointerEvent<HTMLElement>) => {
    x?.(e);
    y?.(e);
  };

function mergePointerHandlers(a: PointerHandlers, b: PointerHandlers): PointerHandlers {
  return {
    onPointerDown: both(a.onPointerDown, b.onPointerDown),
    onPointerMove: both(a.onPointerMove, b.onPointerMove),
    onPointerUp: both(a.onPointerUp, b.onPointerUp),
    onPointerCancel: both(a.onPointerCancel, b.onPointerCancel),
  };
}

export function ListingCard({ listing, anchor, onClose, variant }: ListingCardProps) {
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<Set<number>>(new Set());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const galleryRef = useRef<HTMLDivElement>(null);

  // Mirror the browser's own state rather than assuming ours: the user can leave
  // fullscreen with the system back gesture or Escape, and we'd never hear about
  // it. Unlocking here means the phone is free to rotate again on the way out.
  useEffect(() => {
    const sync = () => {
      const on = document.fullscreenElement !== null;
      setIsFullscreen(on);
      if (!on) screen.orientation?.unlock?.();
    };
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  /**
   * Fullscreen the photo and turn the phone sideways. A listing photo is
   * invariably landscape and the phone invariably is not, so portrait wastes
   * most of the frame on letterboxing.
   *
   * Orientation lock is only permitted from inside fullscreen, so the order
   * matters — and it is unimplemented on iOS, where this degrades to plain
   * fullscreen rather than failing.
   */
  const enterFullscreen = async () => {
    const el = galleryRef.current;
    if (!el) return;
    try {
      await el.requestFullscreen();
      await screen.orientation?.lock?.("landscape");
    } catch {
      // A rejected lock still leaves a usable fullscreen photo behind.
    }
  };

  // A different listing means a fresh gallery.
  useEffect(() => {
    setIndex(0);
    setFailed(new Set());
  }, [listing.id]);

  const count = listing.photos.length;
  const hasPhoto = count > 0 && failed.size < count;
  const days = ageInDays(listing.postedAt);
  const tone = ageColor(days);

  // Clamped rather than wrapped: the strip slides, and wrapping from the last
  // photo to the first would have to rewind visibly across every slide between.
  const step = (delta: number) => {
    setIndex((i) => Math.min(count - 1, Math.max(0, i + delta)));
  };

  // Horizontal swipe pages the gallery. Axis-locked, so a vertical drag that
  // starts on the photo still reaches the sheet's swipe-to-dismiss underneath.
  // `track` lets the strip follow the finger instead of jumping at the threshold.
  const swipe = useSwipe({
    axis: "x",
    threshold: 40,
    track: true,
    onSwipe: (dir) => step(dir === "left" ? 1 : -1),
  });

  // Zooming is a fullscreen affordance: in the card the photo is a thumbnail and
  // the gesture would fight swipe-to-page for no gain.
  const pinch = usePinchZoom({ enabled: isFullscreen });

  // A magnified photo owns single-finger drags — they pan it. Paging would
  // otherwise fire the moment you tried to look at the left half of the image.
  const paging = count > 1 && !pinch.zoomed;

  // Moving to another photo starts it at 1:1.
  const { reset: resetZoom } = pinch;
  useEffect(() => {
    resetZoom();
  }, [index, resetZoom]);

  return (
    <div
      className={cn(
        "relative text-chalk",
        variant === "popup"
          ? // Wide enough for the photo to be worth looking at. A desktop window
            // has the room and the map behind it is still legible around a card
            // this size; the clamp is only there for a narrow window, where the
            // card would otherwise run off both edges at once.
            "console console-grid w-[min(30rem,calc(100vw-2rem))] overflow-hidden rounded-console"
          : // The sheet owns its own chrome, scrolling and handle — see
            // DetentSheet — so here the card is just the content that goes in it.
            "w-full",
      )}
    >
      {/* No freshness bar on either variant. It restated a colour the age badge
          over the photo is already carrying, and as a hard edge across the top
          of the card it read as chrome rather than as data. */}

      {/* Popup only. The sheet closes by handle or swipe, so a corner button
          there would be a third way to do one thing — and the gallery's own
          arrows and fullscreen control are the only chrome it needs. */}
      {variant === "popup" && (
        <Hint label="Close" side="left">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close listing"
            className="absolute top-[13px] right-2.5 z-20 grid size-7 place-items-center rounded-full border border-rule bg-ink-900/85 text-chalk-dim backdrop-blur transition hover:border-rule-bright hover:text-chalk active:scale-95"
          >
            <X className="size-3.5" />
          </button>
        </Hint>
      )}

      {/* ── gallery ───────────────────────────────────────────────────────
          Both recognisers watch this surface, so they are merged rather than
          spread; `usePinchZoom` no-ops unless enabled, so it can always be in. */}
      <div
        ref={galleryRef}
        {...mergePointerHandlers(paging ? swipe.handlers : {}, pinch.handlers)}
        className={cn(
          "relative w-full overflow-hidden bg-ink-850 transition-[height] duration-200",
          // `touch-none` in both states, for two different reasons.
          //
          // In the card: handing vertical panning back to the browser is what
          // let Android's pull-to-refresh swallow a drag that started on a photo.
          //
          // In fullscreen: the zoom is ours (see usePinchZoom), and a browser
          // that claims any part of a gesture takes the pointer stream with it.
          // Asking for `pinch-zoom` here instead was a dead end — that requests
          // the *page's* zoom, which a browser suppresses for the whole time an
          // element is fullscreen.
          "touch-none",
          // A phone's viewport is the constraint, so the photo gets more of it:
          // taller by default, all of the screen once fullscreen.
          //
          // The popup's `vh` term is what keeps the whole card on screen on a
          // short window. The card is anchored above its dot, so height it
          // cannot afford doesn't clip — it flips the popup below the dot and
          // hangs off the bottom instead.
          isFullscreen ? "h-screen" : variant === "popup" ? "h-[min(18.5rem,32vh)]" : "h-56",
        )}
      >
        {hasPhoto ? (
          /* One strip of all the photos, translated into place. Swapping a single
             <img>'s src instead meant the browser tore down the decoded frame
             before the next one arrived, which is the black flash between slides. */
          <div
            className="flex size-full"
            style={{
              transform: `translateX(calc(${-index * 100}% + ${swipe.offset}px))`,
              transition: swipe.dragging
                ? "none"
                : "transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1)",
            }}
          >
            {listing.photos.map((p, i) => (
              <div key={p} className="size-full shrink-0">
                {failed.has(i) ? (
                  <div className="flex size-full flex-col items-center justify-center gap-1.5 text-chalk-mute">
                    <ImageOff className="size-5" />
                    <span className="eyebrow">unavailable</span>
                  </div>
                ) : (
                  <img
                    src={p}
                    alt=""
                    /* The neighbours are fetched eagerly: inside an overflow-hidden
                       strip they never intersect the viewport, so `lazy` would hold
                       them back until the moment they are already on screen. */
                    loading={Math.abs(i - index) <= 1 ? "eager" : "lazy"}
                    decoding="async"
                    /* Several sources reject requests carrying a foreign referer. */
                    referrerPolicy="no-referrer"
                    /* Only the photo being looked at carries the zoom, so the
                       neighbours stay at 1:1 and are correct the moment you
                       page to them. */
                    style={
                      i === index && isFullscreen
                        ? {
                            transform: pinch.transform,
                            transition: pinch.zoomed ? "none" : undefined,
                          }
                        : undefined
                    }
                    className={cn(
                      "size-full",
                      // Cropping is right in a small frame and wrong in a big
                      // one: fullscreen exists to see the whole photo.
                      isFullscreen ? "object-contain" : "object-cover",
                    )}
                    onError={() => setFailed((f) => new Set(f).add(i))}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex size-full flex-col items-center justify-center gap-1.5 text-chalk-mute">
            <ImageOff className="size-5" />
            <span className="eyebrow">no photo</span>
          </div>
        )}

        {!isFullscreen && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-ink-900 to-transparent" />
        )}

        {/* Fullscreen's only other way out is the system back gesture, which is
            not an affordance — nothing on screen says the photo is escapable.
            Insets, because landscape is where this lives and the notch is then
            down one side. */}
        {isFullscreen && (
          <button
            type="button"
            onClick={() => void document.exitFullscreen()}
            aria-label="Exit fullscreen"
            className="absolute z-30 grid size-9 place-items-center rounded-full border border-rule bg-ink-900/80 text-chalk backdrop-blur transition hover:border-rule-bright active:scale-95"
            style={{
              top: "max(0.75rem, env(safe-area-inset-top))",
              right: "max(0.75rem, env(safe-area-inset-right))",
            }}
          >
            <X className="size-4" />
          </button>
        )}

        {variant === "sheet" && hasPhoto && !isFullscreen && (
          <button
            type="button"
            onClick={() => void enterFullscreen()}
            aria-label="View photo fullscreen"
            className="absolute right-2 bottom-2 z-10 grid size-7 place-items-center rounded-full border border-rule bg-ink-900/80 text-chalk-dim backdrop-blur transition hover:text-chalk active:scale-95"
          >
            <Expand className="size-3.5" />
          </button>
        )}

        {count > 1 && (
          <>
            <GalleryButton side="left" onClick={() => step(-1)} disabled={index === 0}>
              <ChevronLeft className="size-4" />
            </GalleryButton>
            <GalleryButton side="right" onClick={() => step(1)} disabled={index === count - 1}>
              <ChevronRight className="size-4" />
            </GalleryButton>
            <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1">
              {listing.photos.map((p, i) => (
                <span
                  key={p}
                  className={cn(
                    "h-[3px] w-3 rounded-full transition-colors",
                    i === index ? "bg-chalk" : "bg-chalk/25",
                  )}
                />
              ))}
            </div>
          </>
        )}

        <Hint
          label={`${listing.postedApprox ? "First seen" : "Posted"} ${fullDate(listing.postedAt)}`}
          detail={
            listing.postedApprox
              ? "This source publishes no post date, so this is when the ingest first saw it."
              : "Dot colour on the map tracks this age."
          }
          side="bottom"
          align="start"
        >
          {/* The same hairline casing the map pills carry: this badge lands on
              an arbitrary photo, and a bright fill with no edge dissolves into
              a pale wall or a sunlit window. The ring gives it a border at any
              luminance, the shadow lifts it off the surface, and both are drawn
              from the map's own palette so the badge reads as the same object
              as the pill it came from. */}
          <button
            type="button"
            className="absolute left-3 top-3 cursor-default rounded-full px-2 py-0.5 font-mono text-[9px] font-medium uppercase tracking-[0.14em] ring-1 ring-[#04070a]/85 shadow-[0_1px_4px_rgba(0,0,0,0.4)]"
            style={{ background: tone, color: ageInk(tone) }}
          >
            {listing.postedApprox ? "~" : ""}
            {spelledAge(listing.postedAt)}
          </button>
        </Hint>
      </div>

      {/* ── body ────────────────────────────────────────────────────────── */}
      <div className="relative space-y-3 p-3.5 pt-3">
        {/* No "per month" unit: every rent here is monthly, so the label only
            ever restated the obvious and competed with the number. */}
        <span className="tnum block font-mono text-[22px] font-medium leading-none tracking-tight">
          {money(listing.price)}
        </span>

        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[12.5px] text-chalk-dim">
          <Stat icon={<BedDouble className="size-3.5" />}>{bedLabel(listing.beds)}</Stat>
          <Stat icon={<Bath className="size-3.5" />}>{bathLabel(listing.baths)}</Stat>
          {listing.sqft != null && (
            <Stat icon={<Ruler className="size-3.5" />}>{listing.sqft.toLocaleString()} ft²</Stat>
          )}
        </div>

        {/* Keyed on the listing: the card is reconciled rather than remounted
            when the selection changes, so without this the legs stay expanded
            from whichever unit was open before — the same trap the gallery
            index above resets by hand.

            `data-sheet-fold` marks this as the last thing the mobile sheet shows
            at rest. Photo, price, layout and travel times answer the question
            that made someone tap the pin; the address and the outbound links
            are what you go looking for once the answer is yes. */}
        <div data-sheet-fold>
          <TravelPanel key={listing.id} travel={listing.travel} anchor={anchor} />
        </div>

        <div className="space-y-0.5 border-t border-rule pt-2.5">
          {/* Address and neighbourhood share a line: the neighbourhood is one
              short token and had a whole row to itself. `min-w-0` lets the
              address truncate rather than shoving it off the end. */}
          {(listing.address ?? listing.neighborhood) && (
            <div className="flex items-baseline justify-between gap-3">
              <p className="min-w-0 flex-1 truncate text-[13px] font-medium leading-snug text-chalk">
                {listing.address}
              </p>
              {listing.neighborhood && (
                <span className="eyebrow shrink-0">{listing.neighborhood}</span>
              )}
            </div>
          )}
          {/* A title that only restates the layout is dropped — the stats row
              above already says "1 bd · 1 ba". */}
          {listing.title &&
            listing.title !== listing.address &&
            !isLayoutOnlyTitle(listing.title) && (
              <p className="line-clamp-2 text-[12px] leading-snug text-chalk-dim">
                {listing.title}
              </p>
            )}
        </div>

        {/* Track and share sit beside the CTA rather than under it: they're the
            secondary actions, and a second full-width button would read as a
            second destination. */}
        <div className="flex items-stretch gap-2">
          <a
            href={listing.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg bg-signal px-3 py-2.5 text-[13px] font-semibold text-on-accent transition hover:bg-signal-deep focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-ink"
          >
            View on {sourceName(listing.source)}
            <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <TrackButton id={listing.id} />
          <HideButton id={listing.id} onHidden={onClose} />
          <ShareButton listing={listing} />
        </div>

        {listing.alsoOn.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
            <span className="eyebrow">also on</span>
            {listing.alsoOn.map((s) => (
              <a
                key={s.url}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded border border-rule px-1.5 py-0.5 text-[10.5px] text-chalk-dim transition hover:border-rule-bright hover:text-chalk"
              >
                {sourceName(s.source)}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="text-chalk-mute">{icon}</span>
      <span className="tnum">{children}</span>
    </span>
  );
}

function GalleryButton({
  side,
  onClick,
  disabled,
  children,
}: {
  side: "left" | "right";
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={side === "left" ? "Previous photo" : "Next photo"}
      className={cn(
        "absolute top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-full border border-rule bg-ink-900/80 text-chalk-dim backdrop-blur transition hover:text-chalk active:scale-95",
        "disabled:pointer-events-none disabled:opacity-0",
        side === "left" ? "left-2" : "right-2",
      )}
    >
      {children}
    </button>
  );
}
